import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Box from '@mui/joy/Box';
import Button from '@mui/joy/Button';
import FormControl from '@mui/joy/FormControl';
import FormLabel, { formLabelClasses } from '@mui/joy/FormLabel';
import Input from '@mui/joy/Input';
import Link from '@mui/joy/Link';
import Typography from '@mui/joy/Typography';
import RightImagePage from '../layouts/RightImagePage';

import signupImage from '../../assets/signup.jpg';

import { useLogin } from '../../api/user';

// Landing page for the Better-Auth password reset link. The backend reset
// callback redirects here with `?token=...` (or `?error=INVALID_TOKEN`). Setting
// a password here completes the Firebase->Better-Auth bridge for password users:
// resetPassword creates a `credential` account on their existing users row.
export default function ResetPassword({ setError }) {
  const [params] = useSearchParams();
  const token = params.get('token');
  const invalid = !token || !!params.get('error');

  const login = useLogin();
  const [done, setDone] = useState(false);

  const submitHandler = async (event) => {
    event.preventDefault();
    const elements = event.currentTarget.elements;
    const password = elements?.password?.value;
    const confirm = elements?.confirm?.value;
    if (!password || password !== confirm) {
      setError && setError('Passwords do not match');
      return;
    }
    try {
      await login.resetPassword({ token, newPassword: password });
      setDone(true);
    } catch (err) {
      setError && setError(`Password reset failed: ${err?.message || ''}`);
    }
  };

  return (
    <RightImagePage image={signupImage} component="main"
      sx={{
        my: 'auto',
        py: 2,
        pb: 5,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        width: 400,
        maxWidth: '100%',
        mx: 'auto',
        borderRadius: 'sm',
        '& form': {
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        },
        [`& .${formLabelClasses.asterisk}`]: {
          visibility: 'hidden',
        },
      }}>
      <div>
        <Typography component="h1" fontSize="xl2" fontWeight="lg">
          Set a new password
        </Typography>
        <Typography level="body2" sx={{ my: 1, mb: 3 }}>
          {done
            ? 'Your password has been set. You can now sign in.'
            : invalid
              ? 'This reset link is invalid or has expired.'
              : 'Choose a new password for your account.'}
        </Typography>
      </div>

      {done && <Link href="/login" fontWeight="lg">Go to sign in</Link>}
      {!done && invalid &&
        <Link href="/password-reset" fontWeight="lg">Request a new reset link</Link>}
      {!done && !invalid &&
        <form onSubmit={submitHandler}>
          <FormControl required>
            <FormLabel>New password</FormLabel>
            <Input type="password" name="password" />
          </FormControl>
          <FormControl required>
            <FormLabel>Confirm password</FormLabel>
            <Input type="password" name="confirm" />
          </FormControl>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Link fontSize="sm" href="/login" fontWeight="lg">Back to sign in</Link>
          </Box>
          <Button type="submit" fullWidth>Set new password</Button>
        </form>}
    </RightImagePage>
  );
}
